/**
 * Бланк УТОЧНЮЮЧОГО розрахунку з ПДВ (J0217026) — 2026-08-05.
 *
 * Кожен рядок декларації — трійка граф: 4 «показник, який уточнюється»
 * (з поданої декларації), 5 «уточнений показник», 6 «різниця» (рахується
 * АВТОМАТИЧНО = гр.5 − гр.4). Коди XSD: <код>4 / <код> / <код>6.
 * Плюс ряд.18.1 (недоплата) / 18.2 (штраф 3%) і таблиця 2 (виправлення
 * залишку від'ємного значення минулих періодів).
 */
import type { CSSProperties, ReactNode } from 'react';
import type { Organization } from '@/api/catalogs';
import type { VatDeclaration } from '@/api/vatDeclarations';
import DatePopover from '@/components/DatePopover';
import {
  B, MONTHS_UK, SECTION_I, SECTION_II, SECTION_III, SERIF, XMark,
  blankBox, nameCell, numCell, roStyle, thBlank, type RowDef,
} from './VatDeclarationBlank';

const ALL_ROWS: { title: string; rows: RowDef[] }[] = [
  { title: 'Розділ I. ПОДАТКОВІ ЗОБОВ’ЯЗАННЯ', rows: SECTION_I },
  { title: 'Розділ II. ПОДАТКОВИЙ КРЕДИТ', rows: SECTION_II },
  { title: 'Розділ III. РОЗРАХУНКИ ЗА ЗВІТНИЙ ПЕРІОД', rows: SECTION_III },
];

/** Колонки таблиці 2 (порядок sequence XSD J0217026). */
export const UR_T2_COLS: { code: string; label: string; type: string }[] = [
  { code: 'G5D', label: 'Дата подання декларації', type: 'date' },
  { code: 'G6S', label: 'Номер декларації', type: 'str' },
  { code: 'G71', label: 'Місяць періоду', type: 'month' },
  { code: 'G72', label: 'Рік періоду', type: 'year' },
  { code: 'G8', label: 'Збільшення залишку, грн (+)', type: 'int' },
  { code: 'G9', label: 'Зменшення залишку, грн (−)', type: 'int' },
];

const inputStyle: CSSProperties = { width: '100%', boxSizing: 'border-box',
  border: 'none', outline: 'none', fontFamily: SERIF, fontSize: '0.8rem',
  textAlign: 'right', padding: '2px 4px', color: '#111' };

function Cell({ value, readOnly, print, editable, onChange }: {
  value: number | undefined;
  readOnly: boolean;
  print?: boolean;
  editable: boolean;
  onChange?: (raw: string) => void;
}) {
  if (readOnly || !editable || !onChange) {
    return (
      <span style={{ display: 'block', padding: '2px 4px', textAlign: 'right',
        fontFamily: SERIF, fontSize: '0.8rem',
        fontWeight: editable ? 600 : 400,
        background: print ? 'transparent'
          : (editable ? '#eef1f4' : '#f6f7f8') }}>
        {value == null ? '' : value.toLocaleString('uk-UA')}
      </span>
    );
  }
  return (
    <input type="number" step="1" value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, background: 'var(--badge-amber-bg)' }} />
  );
}

/** Шапка УР: тип, період подання, ПЕРІОД ЩО УТОЧНЮЄТЬСЯ, платник. */
export function CorrectionHeader({ doc, org, print }: {
  doc: VatDeclaration;
  org: Organization | undefined;
  print?: boolean;
}) {
  const tin = org?.entity_type === 'FOP' ? (org?.inn || org?.edrpou) : org?.edrpou;
  const ro = (v: string | null | undefined) => (
    <span style={{ ...roStyle, background: print ? 'transparent'
      : (roStyle.background as string) }}>{v || ' '}</span>
  );
  return (
    <div style={{ ...blankBox }}>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.92rem',
        padding: '6px 8px', fontFamily: SERIF }}>
        УТОЧНЮЮЧИЙ РОЗРАХУНОК ПОДАТКОВИХ ЗОБОВ’ЯЗАНЬ З ПОДАТКУ НА ДОДАНУ
        ВАРТІСТЬ У ЗВ’ЯЗКУ З ВИПРАВЛЕННЯМ САМОСТІЙНО ВИЯВЛЕНИХ ПОМИЛОК
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: 46 }} /><col /><col style={{ width: 300 }} />
        </colgroup>
        <tbody>
          <tr>
            <td style={numCell}>01</td>
            <td style={nameCell}>Подається як самостійний документ</td>
            <td style={{ ...nameCell, textAlign: 'center' }}><XMark on /></td>
          </tr>
          <tr>
            <td style={numCell}>02</td>
            <td style={nameCell}>Звітний (податковий) період (подання)</td>
            <td style={{ ...nameCell, textAlign: 'center', fontWeight: 600 }}>
              {MONTHS_UK[doc.period_month - 1]} {doc.period_year}
            </td>
          </tr>
          <tr>
            <td style={numCell}>03</td>
            <td style={nameCell}>
              Звітний (податковий) період, ЩО УТОЧНЮЄТЬСЯ
            </td>
            <td style={{ ...nameCell, textAlign: 'center', fontWeight: 700 }}>
              {doc.corrected_period_month
                ? `${MONTHS_UK[doc.corrected_period_month - 1]} ${doc.corrected_period_year}`
                : '—'}
            </td>
          </tr>
          <tr>
            <td style={numCell}>04</td>
            <td style={nameCell}>Платник</td>
            <td style={nameCell}>{ro(org?.full_name || org?.name)}</td>
          </tr>
          <tr>
            <td style={numCell}>041</td>
            <td style={nameCell}>Індивідуальний податковий номер платника ПДВ</td>
            <td style={{ ...nameCell, textAlign: 'center' }}>{ro(org?.vat_number)}</td>
          </tr>
          <tr>
            <td style={numCell}>042</td>
            <td style={nameCell}>
              {org?.entity_type === 'FOP' ? 'РНОКПП' : 'Код за ЄДРПОУ'}
            </td>
            <td style={{ ...nameCell, textAlign: 'center' }}>{ro(tin)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * Розділи УР: 6 колонок значень (А і Б × було/стало/різниця).
 * setValues — редагування гр.4 і гр.5; гр.6 рахується автоматично.
 */
export function CorrectionSections({ values, onEdit, readOnly, print }: {
  values: Record<string, number>;
  onEdit?: (patch: Record<string, number | null>) => void;
  readOnly: boolean;
  print?: boolean;
}) {
  const edit = (base: string, col: '4' | '5', raw: string) => {
    if (!onEdit) return;
    const code = col === '4' ? `${base}4` : base;
    const n = parseInt(raw, 10);
    const next: Record<string, number | null> = {
      [code]: (!raw || Number.isNaN(n)) ? null : n,
    };
    // гр.6 = гр.5 − гр.4 (авто).
    const g4 = col === '4' ? (next[code] ?? 0) : (values[`${base}4`] ?? 0);
    const g5 = col === '5' ? (next[code] ?? 0) : (values[base] ?? 0);
    const diff = g5 - g4;
    next[`${base}6`] = diff === 0 ? null : diff;
    onEdit(next);
  };

  const triple = (base?: string) => {
    if (!base) {
      return (
        <>
          <td style={{ border: B, textAlign: 'center', color: '#999' }}>×</td>
          <td style={{ border: B, textAlign: 'center', color: '#999' }}>×</td>
          <td style={{ border: B, textAlign: 'center', color: '#999' }}>×</td>
        </>
      );
    }
    const diff = values[`${base}6`];
    return (
      <>
        <td style={{ border: B, padding: '1px 2px' }}>
          <Cell value={values[`${base}4`]} readOnly={readOnly} print={print}
            editable onChange={(raw) => edit(base, '4', raw)} />
        </td>
        <td style={{ border: B, padding: '1px 2px' }}>
          <Cell value={values[base]} readOnly={readOnly} print={print}
            editable onChange={(raw) => edit(base, '5', raw)} />
        </td>
        <td style={{ border: B, padding: '1px 2px',
          background: diff ? (print ? 'transparent' : '#fff7ed') : undefined }}>
          <span style={{ display: 'block', padding: '2px 4px',
            textAlign: 'right', fontFamily: SERIF, fontSize: '0.8rem',
            fontWeight: diff ? 700 : 400,
            color: (diff ?? 0) < 0 ? '#b91c1c' : '#111' }}>
            {diff == null ? '' : diff.toLocaleString('uk-UA')}
          </span>
        </td>
      </>
    );
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
      marginTop: 6 }}>
      <colgroup>
        <col style={{ width: 52 }} /><col />
        {Array.from({ length: 6 }, (_, i) => (
          <col key={i} style={{ width: 92 }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th style={thBlank} rowSpan={2}>Код рядка</th>
          <th style={thBlank} rowSpan={2}>Показник</th>
          <th style={thBlank} colSpan={3}>Колонка А — обсяги, грн</th>
          <th style={thBlank} colSpan={3}>Колонка Б — ПДВ, грн</th>
        </tr>
        <tr>
          <th style={thBlank}>гр.4 було</th>
          <th style={thBlank}>гр.5 стало</th>
          <th style={thBlank}>гр.6 різниця</th>
          <th style={thBlank}>гр.4 було</th>
          <th style={thBlank}>гр.5 стало</th>
          <th style={thBlank}>гр.6 різниця</th>
        </tr>
      </thead>
      <tbody>
        {ALL_ROWS.map((sec) => (
          <SectionRows key={sec.title} title={sec.title} rows={sec.rows}
            triple={triple} print={print} />
        ))}
      </tbody>
    </table>
  );
}

function SectionRows({ title, rows, triple, print }: {
  title: string;
  rows: RowDef[];
  triple: (base?: string) => ReactNode;
  print?: boolean;
}) {
  return (
    <>
      <tr>
        <td colSpan={8} style={{ ...nameCell, fontWeight: 700,
          textAlign: 'center', background: print ? '#fff' : '#f8f8f6' }}>
          {title}
        </td>
      </tr>
      {rows.map((r) => (
        <tr key={r.num} style={{ fontWeight: r.bold ? 700 : 400 }}>
          <td style={numCell}>{r.num}</td>
          <td style={nameCell}>{r.label}</td>
          {triple(r.a)}
          {triple(r.b)}
        </tr>
      ))}
    </>
  );
}

/** Блок 18.1/18.2: недоплата + штраф 3% при самостійному виправленні. */
export function CorrectionFineBlock({ values, setV, readOnly, print }: {
  values: Record<string, number>;
  setV?: (code: string, raw: string) => void;
  readOnly: boolean;
  print?: boolean;
}) {
  const underpay = values['R181GB6'] ?? 0;
  const suggestedFine = Math.round(underpay * 0.03);
  const cell = (code: string) => (
    <td style={{ border: B, padding: '1px 2px', width: 120 }}>
      <Cell value={values[code]} readOnly={readOnly} print={print} editable
        onChange={setV ? (raw) => setV(code, raw) : undefined} />
    </td>
  );
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
      marginTop: 6 }}>
      <tbody>
        <tr>
          <td style={numCell}>18.1</td>
          <td style={nameCell}>
            Сума недоплати (збільшення податкового зобов’язання), яка
            сплачується до бюджету у зв’язку з виправленням помилки
          </td>
          {cell('R181GB6')}
        </tr>
        <tr>
          <td style={numCell}>18.2</td>
          <td style={nameCell}>
            Сума штрафу (3% від суми недоплати при поданні уточнюючого
            розрахунку як самостійного документа)
            {!readOnly && !print && underpay > 0 && (
              <span style={{ color: 'var(--badge-amber-fg)', fontFamily: 'inherit' }}>
                {' '}— підказка: {suggestedFine.toLocaleString('uk-UA')} грн
              </span>
            )}
          </td>
          {cell('R182GB6')}
        </tr>
      </tbody>
    </table>
  );
}

/** Таблиця 2 — виправлення залишку від’ємного значення минулих періодів. */
export function CorrectionT2({ rows, onChange, readOnly, print }: {
  rows: Record<string, string | number | null>[];
  onChange?: (rows: Record<string, string | number | null>[]) => void;
  readOnly: boolean;
  print?: boolean;
}) {
  if (print && rows.length === 0) return null;
  const change = onChange ?? (() => undefined);
  const totalG9 = rows.reduce((s, r) => s + (Number(r['G9']) || 0), 0);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: '0.82rem',
        margin: '0 0 3px' }}>
        Таблиця 2. Збільшення/зменшення залишку від’ємного значення, що
        зараховується до складу податкового кредиту наступних періодів
        (виправлення ряд. 21 минулих декларацій)
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
        <thead>
          <tr>
            <th style={{ ...thBlank, width: 30 }}>№</th>
            {UR_T2_COLS.map((c) => (
              <th key={c.code} style={thBlank} title={c.code}>{c.label}</th>
            ))}
            {!readOnly && <th style={{ ...thBlank, width: 28 }}></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td style={{ border: B, textAlign: 'center', fontFamily: SERIF,
                fontSize: '0.8rem' }}>{i + 1}</td>
              {UR_T2_COLS.map((c) => (
                <td key={c.code} style={{ border: B, padding: '1px 2px' }}>
                  {readOnly ? (
                    <span style={{ display: 'block', padding: '2px 4px',
                      fontFamily: SERIF, fontSize: '0.8rem',
                      textAlign: c.type === 'int' ? 'right' : 'left',
                      background: print ? 'transparent' : '#eef1f4' }}>
                      {c.type === 'date' && row[c.code]
                        ? String(row[c.code]).split('-').reverse().join('.')
                        : (row[c.code] ?? '')}
                    </span>
                  ) : c.type === 'date' ? (
                    <div style={{ minWidth: 110 }}>
                      <DatePopover value={String(row[c.code] ?? '')}
                        onChange={(v) => change(rows.map((r, j) =>
                          j === i ? { ...r, [c.code]: v || null } : r))} />
                    </div>
                  ) : (
                    <input
                      type={['int', 'month', 'year'].includes(c.type) ? 'number' : 'text'}
                      value={row[c.code] ?? ''}
                      style={{ ...inputStyle, background: 'var(--badge-amber-bg)',
                        textAlign: c.type === 'str' ? 'left' : 'right',
                        minWidth: 70 }}
                      onChange={(e) => change(rows.map((r, j) =>
                        j === i ? { ...r, [c.code]: e.target.value || null } : r))} />
                  )}
                </td>
              ))}
              {!readOnly && (
                <td style={{ border: B, textAlign: 'center' }}>
                  <span style={{ cursor: 'pointer', color: '#dc2626' }}
                    title="прибрати рядок"
                    onClick={() => change(rows.filter((_, j) => j !== i))}>×</span>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={UR_T2_COLS.length + 2}
              style={{ border: B, padding: '4px 6px', color: '#94a3b8',
                fontFamily: SERIF, fontSize: '0.8rem' }}>
              Немає рядків (заповнюється лише при виправленні ряд. 21).
            </td></tr>
          )}
          {rows.length > 0 && (
            <tr style={{ fontWeight: 700 }}>
              <td style={{ border: B, textAlign: 'center', fontFamily: SERIF,
                fontSize: '0.8rem' }} colSpan={UR_T2_COLS.length}>
                Усього (ряд. 22, гр. «зменшення»)
              </td>
              <td style={{ border: B, padding: '2px 4px', textAlign: 'right',
                fontFamily: SERIF, fontSize: '0.8rem' }}>
                {totalG9.toLocaleString('uk-UA')}
              </td>
              {!readOnly && <td style={{ border: B }}></td>}
            </tr>
          )}
        </tbody>
      </table>
      {!readOnly && (
        <button style={{ marginTop: 3 }}
          onClick={() => change([...rows, {}])}>+ рядок</button>
      )}
    </div>
  );
}
